// =====================================================================
// Locale store. Initial value is detected from localStorage →
// Telegram user.language_code → navigator.language → "ru". The store
// is the single source of truth for the active locale; i18n listens
// via `i18n.on("languageChanged")` so consumers using `useTranslation`
// re-render automatically.
// =====================================================================

import { create } from "zustand";
import { i18n, pickInitialLocale, type Locale } from "@/shared/i18n/i18n";

export type { Locale };

const STORAGE_KEY = "eurostyle_locale_v1";

interface LocaleState {
  locale: Locale;
  setLocale: (l: Locale) => void;
}

const initial = pickInitialLocale();
void i18n.changeLanguage(initial);

function persist(l: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, l);
  } catch {
    /* storage blocked */
  }
}

export const useLocaleStore = create<LocaleState>()((set) => ({
  locale: initial,
  setLocale: (locale) => {
    void i18n.changeLanguage(locale);
    persist(locale);
    set({ locale });
  },
}));

export const useLocale = (): Locale => useLocaleStore((s) => s.locale);
export const useSetLocale = (): ((l: Locale) => void) => useLocaleStore((s) => s.setLocale);
